import React from "react";
import {
  Tooltip as MuiTooltip,
  TooltipProps,
  useTheme,
  tooltipClasses,
  SxProps,
  Theme,
} from "@mui/material";
import { convertStringToSentenceCase } from "utilz/helpers";

interface CustomTooltipProps extends TooltipProps {
  popperStyle?: SxProps<Theme>;
  disableSentenceCase?: boolean;
}
export const Tooltip = ({
  title,
  followCursor = false,
  placement = "top",
  children,
  popperStyle,
  enterDelay,
  leaveDelay,
  enterNextDelay,
  disableSentenceCase = false,

  ...rest
}: CustomTooltipProps) => {
  const theme = useTheme();

  // Deep-merge popperStyle so overrides to `.MuiTooltip-tooltip` extend the
  // base bubble styles instead of replacing them (shallow spread would drop
  // backgroundColor/color). Root-level popper styles pass through untouched.
  const popperStyleObj =
    popperStyle && typeof popperStyle === "object" && !Array.isArray(popperStyle)
      ? (popperStyle as Record<string, unknown>)
      : {};
  const { "& .MuiTooltip-tooltip": tooltipOverride, ...restPopperStyle } =
    popperStyleObj;

  // Flip to the opposite side when there isn't enough room for `placement`
  // (e.g. a bottom-placed tooltip on the last row flips to top, a top-placed
  // one on the first row flips to bottom).
  const fallbackPlacements = placement.startsWith("bottom")
    ? ["top", "bottom"]
    : ["bottom", "top"];

  return (
    <MuiTooltip
      title={
        typeof title === "string" && !disableSentenceCase
          ? convertStringToSentenceCase(title)
          : title
      }
      placement={placement}
      followCursor={followCursor}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      enterNextDelay={enterNextDelay}
      PopperProps={{
        // Enable flip so the tooltip renders on the opposite side when there
        // isn't enough room for `placement` (e.g. first/last rows of a table),
        // and keep it within the viewport.
        modifiers: [
          {
            name: "flip",
            enabled: true,
            options: { fallbackPlacements },
          },
          { name: "preventOverflow", enabled: true },
        ],
        sx: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: "#000",
            ...theme.typography["caption"],
            color: "#FFF",
            ...(tooltipOverride as Record<string, unknown>),
          },
          ...restPopperStyle,
        },
      }}
      {...rest}
    >
      {children}
    </MuiTooltip>
  );
};
